import React, { useState, useEffect } from "react";
import StatsCard from "./StatsCard";
import api from "../utils/api";
import "../Admin.css";

const Dashboard = () => {
  const [refreshKey] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    totalValue: 0,
    categories: 0,
    blogPosts: 0,
    avgDiscount: 0,
    storeCount: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentOffers, setRecentOffers] = useState([]);
  const [topLojas, setTopLojas] = useState([]);
  const [topCategorias, setTopCategorias] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await api.get("/ofertas");
        const offers = response.data;

        const total = offers.length;
        const active = offers.filter(o => o.status === "ATIVA").length;
        const totalValue = offers.reduce((acc, curr) => acc + (parseFloat(curr.preco_desconto) || parseFloat(curr.precoOriginal) || 0), 0);
        
        // Calcular desconto médio
        const discounts = offers
          .filter(o => o.preco_original > 0 && o.preco_desconto > 0)
          .map(o => ((o.preco_original - o.preco_desconto) / o.preco_original) * 100);
        const avgDiscount = discounts.length > 0 
          ? (discounts.reduce((a, b) => a + b, 0) / discounts.length).toFixed(1) 
          : 0;

        // Distribuição por loja
        const stores = {};
        offers.forEach(o => {
          const nomeLoja = o.loja || "Outras";
          stores[nomeLoja] = (stores[nomeLoja] || 0) + 1;
        });
        const sortedStores = Object.entries(stores)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);
        setTopLojas(sortedStores);

        // Distribuição por Categoria
        const catCounts = {};
        offers.forEach(o => {
          const cat = o.categoria || "Outras";
          catCounts[cat] = (catCounts[cat] || 0) + 1;
        });
        const sortedCats = Object.entries(catCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 5);
        setTopCategorias(sortedCats);

        // Ofertas Recentes
        const sortedOffers = [...offers]
          .sort((a, b) => (b.data_cadastro || b.id).localeCompare(a.data_cadastro || a.id))
          .slice(0, 5);
        setRecentOffers(sortedOffers);

        const blogResponse = await api.get("/blog");
        const categoriesResponse = await api.get("/categorias").catch(() => ({ data: [] }));

        setStats({
          total,
          active,
          totalValue,
          categories: categoriesResponse.data.length || Object.keys(catCounts).length,
          blogPosts: blogResponse.data.length,
          avgDiscount,
          storeCount: Object.keys(stores).length
        });

        setRecentPosts(blogResponse.data.slice(0, 5));
      } catch (error) {
        console.error("Erro ao carregar estatísticas", error);
      }
    };
    fetchStats();
  }, [refreshKey]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '30px', paddingBottom: '40px' }}>
      <div className="page-header" style={{ marginBottom: 0 }}>
        <h2>Dashboard Analítico</h2>
      </div>

      {/* Stats Grid 1 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '20px' 
      }}>
        <StatsCard
          title="Total de Ofertas"
          value={stats.total}
          icon="fas fa-box-open"
          color="#12a046"
        />
        <StatsCard
          title="Ofertas Ativas"
          value={stats.active}
          icon="fas fa-check-circle"
          color="#059669"
        />
        <StatsCard
          title="Valor em Estoque"
          value={new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(stats.totalValue)}
          icon="fas fa-dollar-sign"
          color="#3b82f6"
        />
        <StatsCard
          title="Desconto Médio"
          value={`${stats.avgDiscount}%`}
          icon="fas fa-percentage"
          color="#f59e0b"
        />
      </div>

      {/* Stats Grid 2 */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: '20px' 
      }}>
        <StatsCard
          title="Artigos no Blog"
          value={stats.blogPosts}
          icon="fas fa-newspaper"
          color="#6366f1"
        />
        <StatsCard
          title="Categorias"
          value={stats.categories}
          icon="fas fa-tags"
          color="#8b5cf6"
        />
        <StatsCard
          title="Lojas Parceiras"
          value={stats.storeCount}
          icon="fas fa-store"
          color="#64748b"
        />
      </div>

      {/* Seção de Gráficos Visuais (Barras CSS Animadas) */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
        gap: '24px' 
      }}>
        {/* Gráfico Top Lojas */}
        <div style={{ 
          background: 'white', 
          padding: '24px', 
          borderRadius: '16px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.06)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-chart-bar" style={{ color: 'var(--primary-color)' }}></i> Top Lojas por Volume
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {topLojas.map(([loja, count]) => {
              const max = topLojas[0]?.[1] || 1;
              const percentage = (count / max) * 100;
              return (
                <div key={loja} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                    <span>{loja}</span>
                    <span style={{ color: 'var(--primary-color)', fontWeight: '700' }}>{count} ofertas</span>
                  </div>
                  <div style={{ width: '100%', height: '10px', backgroundColor: '#f1f5f9', borderRadius: '5px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${percentage}%`, 
                      height: '100%', 
                      backgroundColor: 'var(--primary-color)', 
                      borderRadius: '5px',
                      transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' 
                    }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Gráfico Distribuição por Categoria */}
        <div style={{ 
          background: 'white', 
          padding: '24px', 
          borderRadius: '16px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.06)'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-chart-pie" style={{ color: '#8b5cf6' }}></i> Distribuição por Categoria
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {topCategorias.map(([cat, count]) => {
              const max = topCategorias[0]?.[1] || 1;
              const percentage = (count / max) * 100;
              return (
                <div key={cat} style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', fontWeight: '600', color: '#475569' }}>
                    <span>{cat}</span>
                    <span style={{ color: '#8b5cf6', fontWeight: '700' }}>{count} produtos</span>
                  </div>
                  <div style={{ width: '100%', height: '10px', backgroundColor: '#f1f5f9', borderRadius: '5px', overflow: 'hidden' }}>
                    <div style={{ 
                      width: `${percentage}%`, 
                      height: '100%', 
                      backgroundColor: '#8b5cf6', 
                      borderRadius: '5px',
                      transition: 'width 1s cubic-bezier(0.4, 0, 0.2, 1)' 
                    }}></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Seção de Tabelas Informativas */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', 
        gap: '24px' 
      }}>
        {/* Ofertas Recentes */}
        <div style={{ 
          background: 'white', 
          padding: '24px', 
          borderRadius: '16px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.06)',
          overflowX: 'auto'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-tags" style={{ color: '#12a046' }}></i> Últimas Ofertas Cadastradas
          </h3>
          <table className="admin-table" style={{ width: '100%', minWidth: '350px' }}>
            <thead>
              <tr>
                <th>Produto</th>
                <th>Loja</th>
                <th>Preço</th>
              </tr>
            </thead>
            <tbody>
              {recentOffers.map(o => (
                <tr key={o.id}>
                  <td style={{ fontWeight: '500', color: '#334155' }}>{o.titulo}</td>
                  <td><span style={{ background: '#f1f5f9', padding: '4px 8px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>{o.loja}</span></td>
                  <td style={{ fontWeight: '700', color: '#12a046' }}>R$ {o.preco_desconto}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Artigos Recentes */}
        <div style={{ 
          background: 'white', 
          padding: '24px', 
          borderRadius: '16px', 
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          border: '1px solid rgba(0,0,0,0.06)',
          overflowX: 'auto'
        }}>
          <h3 style={{ fontSize: '18px', fontWeight: '700', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <i className="fas fa-newspaper" style={{ color: '#6366f1' }}></i> Artigos Recentes no Blog
          </h3>
          <table className="admin-table" style={{ width: '100%', minWidth: '350px' }}>
            <thead>
              <tr>
                <th>Título</th>
                <th>Data</th>
              </tr>
            </thead>
            <tbody>
              {recentPosts.map(post => (
                <tr key={post.id}>
                  <td style={{ fontWeight: '500', color: '#334155' }}>{post.title}</td>
                  <td style={{ color: '#64748b', fontSize: '13px' }}>{new Date(post.createdAt).toLocaleDateString('pt-BR')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
